import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getApiBaseUrl } from '../../config';
import { Spreadsheet } from '../../spreadsheet/src';
import { ColumnDef } from '@tanstack/react-table';

interface APILog {
  id: string; // Required by Spreadsheet
  _id: string;
  apiType: string;
  queryType: string;
  queryDetails: any;
  cost: number;
  timestamp: string;
  formattedTime: string;
  formattedDate: string;
  success: boolean;
  errorMessage?: string;
  resultCount?: number;
  executedBy?: string;
}

interface APISummary {
  totalCost: number;
  totalCalls: number;
  byApiType: Record<string, { calls: number; cost: number }>;
  byQueryType: Record<string, { calls: number; cost: number }>;
}

type TimeFilter = 'today' | '7d' | '30d' | 'all';

// Sample data for testing
const SAMPLE_DATA: APILog[] = [
  {
    id: '1',
    _id: '1',
    apiType: 'places_api',
    queryType: 'nearby_search',
    queryDetails: { latitude: 40.7589, longitude: -73.9851, radiusMeters: 1000, includedTypes: ['restaurant'] },
    cost: 0.032,
    timestamp: new Date().toISOString(),
    formattedTime: '2:30 PM',
    formattedDate: '11/07/2025',
    success: true,
    resultCount: 15,
    executedBy: 'import-google-places',
  },
  {
    id: '2',
    _id: '2',
    apiType: 'places_api',
    queryType: 'place_details',
    queryDetails: { placeId: 'ChIJN1t_tDeuEmsRUsoyG83frY4', placeName: 'Times Square' },
    cost: 0.017,
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    formattedTime: '2:25 PM',
    formattedDate: '11/07/2025',
    success: true,
    resultCount: 1,
    executedBy: 'place-import',
  },
  {
    id: '3',
    _id: '3',
    apiType: 'photo_api',
    queryType: 'photo_download',
    queryDetails: { photoName: 'manhattan-skyline.jpg', maxWidth: 400 },
    cost: 0.007,
    timestamp: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    formattedTime: '2:20 PM',
    formattedDate: '11/07/2025',
    success: true,
    executedBy: 'photo-proxy',
  },
  {
    id: '4',
    _id: '4',
    apiType: 'places_api',
    queryType: 'nearby_search',
    queryDetails: { latitude: 40.7128, longitude: -74.0060, radiusMeters: 500, includedTypes: ['cafe'] },
    cost: 0.032,
    timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    formattedTime: '2:15 PM',
    formattedDate: '11/07/2025',
    success: true,
    resultCount: 8,
    executedBy: 'location-photo-fetch',
  },
  {
    id: '5',
    _id: '5',
    apiType: 'photo_api',
    queryType: 'photo_download',
    queryDetails: { photoName: 'brooklyn-bridge.jpg', maxWidth: 400 },
    cost: 0.007,
    timestamp: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
    formattedTime: '2:10 PM',
    formattedDate: '11/07/2025',
    success: true,
    executedBy: 'photo-proxy',
  },
  {
    id: '6',
    _id: '6',
    apiType: 'places_api',
    queryType: 'place_details',
    queryDetails: { placeId: 'ChIJOwg_06VPwokRYv534QaPC8g', placeName: 'Central Park' },
    cost: 0.017,
    timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
    formattedTime: '2:05 PM',
    formattedDate: '11/07/2025',
    success: false,
    errorMessage: 'Rate limit exceeded',
    executedBy: 'place-import',
  },
];

const APIReportsTab: React.FC = () => {
  const [logs, setLogs] = useState<APILog[]>([]);
  const [summary, setSummary] = useState<APISummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('today');
  const [useSampleData, setUseSampleData] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});

  const fetchAPILogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use sample data if toggle is on
      if (useSampleData) {
        console.log('[APIReports] Using sample data');
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading
        setLogs(SAMPLE_DATA);
        
        // Calculate summary for sample data
        const summaryData: APISummary = {
          totalCost: 0,
          totalCalls: SAMPLE_DATA.length,
          byApiType: {},
          byQueryType: {},
        };

        SAMPLE_DATA.forEach((log: APILog) => {
          summaryData.totalCost += log.cost;

          if (!summaryData.byApiType[log.apiType]) {
            summaryData.byApiType[log.apiType] = { calls: 0, cost: 0 };
          }
          summaryData.byApiType[log.apiType].calls++;
          summaryData.byApiType[log.apiType].cost += log.cost;

          if (!summaryData.byQueryType[log.queryType]) {
            summaryData.byQueryType[log.queryType] = { calls: 0, cost: 0 };
          }
          summaryData.byQueryType[log.queryType].calls++;
          summaryData.byQueryType[log.queryType].cost += log.cost;
        });

        setSummary(summaryData);
        setLoading(false);
        return;
      }

      const apiBaseUrl = getApiBaseUrl();

      console.log('[APIReports] Fetching logs from:', `${apiBaseUrl}/api/admin/google-api-logs?filter=${timeFilter}`);

      // Fetch logs
      const logsResponse = await fetch(`${apiBaseUrl}/api/admin/google-api-logs?filter=${timeFilter}`, {
        credentials: 'include', // Required to send HttpOnly cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('[APIReports] Response status:', logsResponse.status);

      if (!logsResponse.ok) {
        const errorText = await logsResponse.text();
        console.error('[APIReports] Error response:', errorText);
        throw new Error(`Failed to fetch API logs: ${logsResponse.status} ${logsResponse.statusText}`);
      }

      const logsData = await logsResponse.json();
      console.log('[APIReports] Received logs:', logsData.count || 0);
      // Add id field for Spreadsheet component
      const logsWithId = (logsData.logs || []).map((log: any) => ({
        ...log,
        id: log._id,
      }));
      setLogs(logsWithId);

      // Calculate summary
      const summaryData: APISummary = {
        totalCost: 0,
        totalCalls: logsData.logs?.length || 0,
        byApiType: {},
        byQueryType: {},
      };

      logsData.logs?.forEach((log: APILog) => {
        summaryData.totalCost += log.cost;

        // By API Type
        if (!summaryData.byApiType[log.apiType]) {
          summaryData.byApiType[log.apiType] = { calls: 0, cost: 0 };
        }
        summaryData.byApiType[log.apiType].calls++;
        summaryData.byApiType[log.apiType].cost += log.cost;

        // By Query Type
        if (!summaryData.byQueryType[log.queryType]) {
          summaryData.byQueryType[log.queryType] = { calls: 0, cost: 0 };
        }
        summaryData.byQueryType[log.queryType].calls++;
        summaryData.byQueryType[log.queryType].cost += log.cost;
      });

      setSummary(summaryData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, useSampleData]);

  useEffect(() => {
    fetchAPILogs();
  }, [fetchAPILogs]);

  // Apply filters to logs
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // API Type filter
      if (columnFilters.apiType && columnFilters.apiType.length > 0) {
        if (!columnFilters.apiType.includes(log.apiType)) {
          return false;
        }
      }
      
      // Query Type filter
      if (columnFilters.queryType && columnFilters.queryType.length > 0) {
        if (!columnFilters.queryType.includes(log.queryType)) {
          return false;
        }
      }
      
      // Executed By filter
      if (columnFilters.executedBy && columnFilters.executedBy.length > 0) {
        if (!log.executedBy || !columnFilters.executedBy.includes(log.executedBy)) {
          return false;
        }
      }
      
      // Success filter
      if (columnFilters.success && columnFilters.success.length > 0) {
        const successStr = log.success ? 'Success' : 'Failed';
        if (!columnFilters.success.includes(successStr)) {
          return false;
        }
      }
      
      return true;
    });
  }, [logs, columnFilters]);

  // Get unique values for filterable columns (from all logs, not filtered)
  const uniqueValues = useMemo(() => {
    const values: Record<string, string[]> = {
      apiType: [],
      queryType: [],
      executedBy: [],
      success: [],
    };

    logs.forEach((log) => {
      if (log.apiType && !values.apiType.includes(log.apiType)) {
        values.apiType.push(log.apiType);
      }
      if (log.queryType && !values.queryType.includes(log.queryType)) {
        values.queryType.push(log.queryType);
      }
      if (log.executedBy && !values.executedBy.includes(log.executedBy)) {
        values.executedBy.push(log.executedBy);
      }
      const successStr = log.success ? 'Success' : 'Failed';
      if (!values.success.includes(successStr)) {
        values.success.push(successStr);
      }
    });

    return values;
  }, [logs]);

  const columns: ColumnDef<APILog>[] = useMemo(() => [
    {
      accessorKey: 'formattedDate',
      header: 'Date',
      size: 100,
      minSize: 90,
      maxSize: 120,
      enableSorting: true,
    },
    {
      accessorKey: 'formattedTime',
      header: 'Time',
      size: 90,
      minSize: 80,
      maxSize: 100,
      enableSorting: true,
    },
    {
      accessorKey: 'apiType',
      header: 'API Type',
      size: 120,
      minSize: 100,
      maxSize: 140,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return (
          <Chip 
            label={value} 
            size="small" 
            color={value === 'places_api' ? 'primary' : 'secondary'}
          />
        );
      },
    },
    {
      accessorKey: 'queryType',
      header: 'Query Type',
      size: 130,
      minSize: 110,
      maxSize: 150,
      enableSorting: true,
    },
    {
      accessorKey: 'executedBy',
      header: 'Executed By',
      size: 140,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
    },
    {
      accessorKey: 'cost',
      header: 'Cost',
      size: 90,
      minSize: 80,
      maxSize: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const cost = getValue() as number;
        return <span>${cost?.toFixed(4) || '0.0000'}</span>;
      },
    },
    {
      accessorKey: 'success',
      header: 'Status',
      size: 100,
      minSize: 90,
      maxSize: 110,
      enableSorting: true,
      cell: ({ getValue }) => {
        const success = getValue() as boolean;
        return (
          <Chip 
            label={success ? 'Success' : 'Failed'} 
            size="small" 
            color={success ? 'success' : 'error'}
          />
        );
      },
    },
    {
      accessorKey: 'resultCount',
      header: 'Results',
      size: 80,
      minSize: 70,
      maxSize: 90,
      enableSorting: true,
      cell: ({ getValue }) => {
        const count = getValue() as number | undefined;
        return <span>{count || '-'}</span>;
      },
    },
    {
      accessorKey: 'queryDetails',
      header: 'Details',
      size: 200,
      minSize: 150,
      enableSorting: false,
      cell: ({ getValue }) => {
        const details = getValue();
        return (
          <Box
            sx={{
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {JSON.stringify(details)}
          </Box>
        );
      },
    },
  ], []);

  // Render header with toggle button for all states
  const renderHeader = () => (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography variant="h5">Google Places API Usage</Typography>
        {useSampleData && (
          <Chip 
            label="Using Sample Data" 
            color="warning" 
            size="small" 
            sx={{ mt: 1 }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant={useSampleData ? 'contained' : 'outlined'}
          color={useSampleData ? 'warning' : 'primary'}
          size="small"
          onClick={() => setUseSampleData(!useSampleData)}
        >
          {useSampleData ? 'Use Real Data' : 'Use Sample Data'}
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={fetchAPILogs}
          size="small"
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {renderHeader()}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {renderHeader()}
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error Loading API Logs</Typography>
          <Typography variant="body2">{error}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Check the browser console for more details.
          </Typography>
        </Alert>
        <Button variant="outlined" onClick={fetchAPILogs}>
          Retry
        </Button>
      </Box>
    );
  }

  // Show empty state if no logs
  if (!loading && logs.length === 0) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {renderHeader()}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>No API Logs Found</Typography>
          <Typography variant="body2">
            No Google Places API calls have been logged yet for the selected time period.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Try selecting a different time range or make some API calls first.
          </Typography>
        </Alert>
        <ButtonGroup variant="outlined" size="small">
          <Button 
            variant={timeFilter === 'today' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('today')}
          >
            Today
          </Button>
          <Button 
            variant={timeFilter === '7d' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('7d')}
          >
            7 Days
          </Button>
          <Button 
            variant={timeFilter === '30d' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('30d')}
          >
            30 Days
          </Button>
          <Button 
            variant={timeFilter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('all')}
          >
            All Time
          </Button>
        </ButtonGroup>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Header with filters */}
      {renderHeader()}

      {/* Time filters */}
      <Box sx={{ mb: 3, width: '100%', boxSizing: 'border-box' }}>
        <ButtonGroup variant="outlined" size="small">
          <Button 
            variant={timeFilter === 'today' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('today')}
          >
            Today
          </Button>
          <Button 
            variant={timeFilter === '7d' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('7d')}
          >
            7 Days
          </Button>
          <Button 
            variant={timeFilter === '30d' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('30d')}
          >
            30 Days
          </Button>
          <Button 
            variant={timeFilter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setTimeFilter('all')}
          >
            All Time
          </Button>
        </ButtonGroup>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3, width: '100%', boxSizing: 'border-box' }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Calls
                </Typography>
                <Typography variant="h4">
                  {summary.totalCalls.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Cost
                </Typography>
                <Typography variant="h4">
                  ${summary.totalCost.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Places API
                </Typography>
                <Typography variant="h6">
                  {summary.byApiType['places_api']?.calls || 0} calls
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${(summary.byApiType['places_api']?.cost || 0).toFixed(4)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Photo API
                </Typography>
                <Typography variant="h6">
                  {summary.byApiType['photo_api']?.calls || 0} calls
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${(summary.byApiType['photo_api']?.cost || 0).toFixed(4)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Query Type Breakdown */}
      {summary && Object.keys(summary.byQueryType).length > 0 && (
        <Paper sx={{ p: 2, mb: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <Typography variant="h6" gutterBottom>
            Query Type Breakdown
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(summary.byQueryType).map(([queryType, data]) => (
              <Grid item xs={12} sm={6} md={4} key={queryType}>
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {queryType}
                  </Typography>
                  <Typography variant="h6">
                    {data.calls} calls
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${data.cost.toFixed(4)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Spreadsheet */}
      <Box sx={{ height: '600px', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
        <Spreadsheet
          data={filteredLogs}
          columns={columns}
          enableFiltering={true}
          filterableColumns={['apiType', 'queryType', 'executedBy', 'success']}
          uniqueValues={uniqueValues}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
        />
      </Box>
    </Box>
  );
};

export default APIReportsTab;

